import React, { useState } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import moment from 'moment';
import Select, { SingleValue } from 'react-select';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Img } from 'react-image';
import { Loader } from 'ui';
import { Dropdown } from 'shared-utils';
import { GitHubContribution, GitHubPinnedRepository } from 'types';
import { useGetGitHubContributions } from 'src/hooks/github.hooks';
import { Button } from 'flowbite-react';

interface Props {
	userId: String;
	isSelf: boolean;
}
const ContributionGraph = ({ userId, isSelf }: Props) => {
	const [contributionDays, setContributionDays] = React.useState<GitHubContribution[]>([]);
	const [totalContribution, setTotalContribution] = useState<number>();
	const [totalRepositories, setTotalRepositories] = useState<GitHubPinnedRepository[]>();
	const [totalPinnedRepositories, setTotalPinnedRepositories] = useState<GitHubPinnedRepository[]>();
	const [repoOption, setRepoOption] = useState<String>('Pinned Repositories');
	const [gitHubUserName, setGitHubUserName] = useState<string>();
	const [repoToBeShown, setRepoToBeShown] = useState<GitHubPinnedRepository[]>([]);
	const [isShowMore, setIsShowMore] = useState(true);

	const github = useGetGitHubContributions(String(userId), ({ data }) => {
		if (data?.contributionDays) {
			setContributionDays(
				data?.contributionDays?.map((day: any) => ({
					date: day.date,
					count: day.count,
				})),
			);
			setTotalContribution(data?.totalContributions ?? 0);
			setTotalRepositories(
				data?.repositories?.map((repo: any) => ({
					...repo,
				})),
			);
			setTotalPinnedRepositories(
				data?.pinnedRepositories?.map((repo: any) => ({
					...repo,
				})),
			);
			setGitHubUserName(data?.userName);
			if (data?.pinnedRepositories) {
				setRepoToBeShown(data?.pinnedRepositories.slice(0, 4));
			}
		}
	});
	if (github.isLoading || github.isFetching) {
		return (
			<div>
				<Loader />
			</div>
		);
	}
	const fromDate = moment().subtract(10, 'months').format('YYYY-MM-DD');
	const toDate = moment().format('YYYY-MM-DD');
	const repoOptions = [
		{ value: 'All Repositories', label: 'All' },
		{ value: 'Pinned Repositories', label: ' Pinned' },
	];

	const handleShowMore = () => {
		if (isShowMore) {
			if (repoOption === 'Pinned Repositories') {
				if (totalPinnedRepositories) {
					setRepoToBeShown(totalPinnedRepositories);
				}
			} else {
				if (totalRepositories) {
					setRepoToBeShown(totalRepositories);
				}
			}
		} else {
			if (repoOption === 'Pinned Repositories') {
				if (totalPinnedRepositories) {
					setRepoToBeShown(totalPinnedRepositories.slice(0, 4));
				}
			} else {
				if (totalRepositories) {
					setRepoToBeShown(totalRepositories.slice(0, 4));
				}
			}
		}
	};

	return (
		<>
			<div className="overflow-auto">
				<div className="mt-2 bg-white dark:bg-inherit rounded-md border-2 border-[#C7C9D91A] px-2 min-w-[600px]">
					<div className={`mt-2 ${!isSelf && 'mt-4'} mb-3 ml-2`}>
						<p className="text-sm font-normal text-black dark:text-white">
							<span className="font-semibold text-[#7777E7]">{totalContribution} </span> Contributions in the last year
						</p>
					</div>

					<div>
						<CalendarHeatmap
							startDate={fromDate}
							endDate={toDate}
							values={contributionDays}
							classForValue={(value: any) => {
								if (!value || value.count === 0) {
									return 'color-github-stats-empty';
								} else if (value.count > 0 && value.count <= 5) {
									return 'color-github-stats-1';
								} else if (value.count > 5 && value.count <= 15) {
									return 'color-github-stats-2';
								} else if (value.count > 15 && value.count <= 25) {
									return 'color-github-stats-3';
								} else if (value.count > 25) {
									return 'color-github-stats-4';
								}
							}}
							tooltipDataAttrs={(value: any) => {
								return {
									'data-tooltip-id': 'data-tip',
									'data-tooltip-content': `${value.count} ${value.count > 1 ? 'contributions' : 'contribution'} on ${value.date
										}`,
								};
							}}
							gutterSize={6}
							showWeekdayLabels={true}
						/>
						<div className="flex items-center flex-row-reverse mt-[-20px] sm:mt-[-10px]">
							<p className="text-xs font-normal text-[#28293D] dark:text-[#b2b2b3]">High</p>
							<div className="flex gap-2 sm:gap-1 m-1 sm:mt-1.5 sm:mr-1">
								<div className="h-3 w-3 sm:h-1.5 sm:w-1.5 bg-[#f2f2f5] dark:bg-[#161b22] rounded-sm sm:rounded-[1px]"></div>
								<div className="h-3 w-3 sm:h-1.5 sm:w-1.5 bg-[#d6e685] dark:bg-[#0e4429] rounded-sm sm:rounded-[1px]"></div>
								<div className="h-3 w-3 sm:h-1.5 sm:w-1.5 bg-[#8cc665] dark:bg-[#006d32] rounded-sm sm:rounded-[1px]"></div>
								<div className="h-3 w-3 sm:h-1.5 sm:w-1.5 bg-[#44a340] dark:bg-[#26a641] rounded-sm sm:rounded-[1px]"></div>
								<div className="h-3 w-3 sm:h-1.5 sm:w-1.5 bg-[#1e6823] dark:bg-[#39d353] rounded-sm sm:rounded-[1px]"></div>
							</div>
							<p className="text-xs font-normal text-[#28293D] dark:text-[#b2b2b3]">Low</p>
						</div>
						<ReactTooltip id="data-tip" />
					</div>
				</div>
			</div>

			<div>
				<div className="mt-2 flex items-center justify-between">
					<p className="mt-2 text-sm text-[#1C1C28] dark:text-white">
						Total{' '}
						<span className="font-semibold text-[#7777E7]">
							{repoOption === 'Pinned Repositories' ? totalPinnedRepositories?.length : totalRepositories?.length}
						</span>{' '}
						repositories
					</p>
					<div className="select-container bg-transparent dark:bg-[#1F2A37] text-[#FAFAFC]">
						<Select
							menuPlacement={'auto'}
							className="react-select-container mt-2 w-[200px] text-[16px] sm:max-w-[120px]"
							classNamePrefix="my-select-plans"
							closeMenuOnSelect
							menuShouldScrollIntoView
							options={repoOptions}
							value={repoOptions?.find(option => option.value === repoOption)}
							onChange={(val: SingleValue<Dropdown>) => {
								setRepoOption(val?.value || '');
								setIsShowMore(true);
								if (val?.value === 'Pinned Repositories') {
									if (totalPinnedRepositories && totalPinnedRepositories.length > 4) {
										setRepoToBeShown(totalPinnedRepositories.slice(0, 4));
									} else {
										if (totalPinnedRepositories) setRepoToBeShown([...totalPinnedRepositories]);
									}
								} else {
									if (totalRepositories && totalRepositories.length > 4) {
										setRepoToBeShown(totalRepositories.slice(0, 4));
									} else {
										if (totalRepositories) setRepoToBeShown([...totalRepositories]);
									}
								}
							}}
						/>
					</div>
				</div>

				<div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-1 sm:gap-y-4">
					{repoToBeShown?.map((pinnedRepo, i) => (
						<div
							key={pinnedRepo.name}
							className="flex min-h-[148px] cursor-pointer justify-between gap-2 bg-white dark:bg-[#18222D] rounded-[7.077px]"
							onClick={() => window.open(`https://github.com/${gitHubUserName}/${pinnedRepo.name}`, '_blank')}
						>
							<div className="h-full flex-1 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
								<div className="flex h-[100%] flex-col justify-between">
									<div className="ml-0 h-[100%]">
										<p className="paragraph-clamp mb-0.5 text-sm font-semibold text-[#28293D] dark:text-[#C7C9D9]">
											{pinnedRepo.name}
										</p>
										<p className="paragraph-clamp break-words text-xs font-normal text-[#28293D] dark:text-[#C7C9D9]">
											{pinnedRepo.description}
										</p>
									</div>

									<div className="flex items-center justify-between">
										<div className="mt-2 flex max-w-[300px] flex-wrap gap-2">
											{pinnedRepo.languages?.map(lang => {
												const color = lang.color || '';
												return (
													<div
														className="flex items-center  gap-2 rounded-2xl border-[1px] border-[#C7C9D91A] px-[6px]"
														key={lang.name}
													>
														<div style={{ background: color }} className={`h-[10px] w-[10px] rounded-full`}></div>
														<div className="text-[10px] text-[#28293D] dark:text-[#C7C9D9]">{lang.name}</div>
													</div>
												);
											})}
										</div>
										<div className="mt-2 flex">
											{typeof pinnedRepo.forks === 'number' && pinnedRepo.forks > 0 && (
												<div className="flex gap-1">
													<div className="mt-1">
														<Img src={'/assets/fork-icon.svg'} alt="github-logo" width={13} height={13} />
													</div>
													<div className="mr-4 h-4 w-4 text-sm font-normal text-[#28293D] dark:text-[#8B949E]">
														{pinnedRepo.forks}
													</div>
												</div>
											)}
											{typeof pinnedRepo.stars === 'number' && pinnedRepo.stars > 0 && (
												<div className="flex gap-1">
													<div className="mt-1">
														<Img src={'/assets/start-icon.svg'} alt="github-logo" width={13} height={13} />
													</div>
													<div className="h-4 w-4 text-sm font-normal text-[#28293D] dark:text-[#8B949E]">
														{pinnedRepo.stars}
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>

				{repoOption === 'Pinned Repositories' ? (
					totalPinnedRepositories && totalPinnedRepositories.length > 4 ? (
						<Button
							className="m-auto mt-4 flex w-[150px] cursor-pointer items-center justify-center"
							color="light"
							outline
							onClick={() => {
								setIsShowMore(prev => !prev);
								handleShowMore();
							}}
						>
							<span className="text-sm">Show {isShowMore ? 'More' : 'Less'}</span>
							<span className="ml-2 ">
								{isShowMore ? (
									<FiChevronDown className="text-sm font-bold" />
								) : (
									<FiChevronUp className="text-sm font-bold" />
								)}
							</span>
						</Button>
					) : null
				) : totalRepositories && totalRepositories.length > 4 ? (
					<Button
						className="m-auto mt-4 flex w-[150px] cursor-pointer items-center justify-center"
						color="light"
						outline
						onClick={() => {
							setIsShowMore(prev => !prev);
							handleShowMore();
						}}
					>
						<span className="text-sm">Show {isShowMore ? 'More' : 'Less'}</span>
						<span className="ml-2 ">
							{isShowMore ? (
								<FiChevronDown className="text-sm font-bold" />
							) : (
								<FiChevronUp className="text-sm font-bold" />
							)}
						</span>
					</Button>
				) : null}
			</div>
		</>
	);
};

export default ContributionGraph;
